import api from '@/services/api';

/**
 * Template images stored in R2.
 *
 * The editor keeps working with `data:` URLs while you author — that is what
 * every insert path produces, and it is what the exporter can draw without
 * worrying about canvas tainting. The lift into R2 happens server-side on save
 * (see utils/printTemplateMedia.js in the API), so a template loaded back from
 * the server has short URLs in place of the base64.
 */

// Three layouts are in the bucket: `<id>/media/<sha>.<ext>` inside the template's
// own folder (current), and two older flat ones. The `sohozkaj/` root is optional
// but must be KEPT when present — slicing it off hands the API a key R2 cannot
// find. Leftmost-match does that for free, since the root sits earlier in the URL.
const MEDIA_KEY = /(?:sohozkaj\/)?print-templates\/(?:\d+\/)?media\/[^?#]+/;

/** The R2 key inside one of our media URLs, or null for anything else. */
export function mediaKeyFromUrl(url) {
    const match = MEDIA_KEY.exec(String(url || ''));
    return match ? match[0] : null;
}

export function isTemplateMediaUrl(url) {
    return !!mediaKeyFromUrl(url);
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('Failed to read image'));
        reader.readAsDataURL(blob);
    });
}

/**
 * Read a stored template image through our own API and hand back a data URL.
 *
 * Used by the exporter when a direct CORS load fails: the bytes come from the
 * same origin as the app, so drawing them onto the export canvas can never
 * taint it, whatever headers the bucket is or is not sending.
 */
export async function fetchMediaAsDataUrl(url) {
    const key = mediaKeyFromUrl(url);
    if (!key) throw new Error('Not a print-template media URL');
    const blob = await api.get('/api/print-templates/media', {
        params: { key },
        responseType: 'blob',
    });
    return blobToDataUrl(blob);
}
